import React from 'react';
import { Grid, Paper, TableCell, TableRow, Typography } from '@mui/material';
import type { UsageExtra, UsageFormReport } from '../types';
import { LEER, formatCount, formatDuration, formatNumber, formatPercent } from '../services/usageFormat';
import SchutzZeile from './SchutzZeile';
import DatenTabelle, { zahlenZelle } from './DatenTabelle';
import { nutzerText } from './SeitenTabelle';

/**
 * RITA-1505 (Nutzungserfassung Phase 2c) — Formulare und Suche.
 *
 * „Abgeschickt“ heißt: an den Server gesendet UND ohne Fehler beantwortet. Abbruch heißt:
 * geöffnet, aber ohne Absenden verlassen. Bei den ungültigen Feldern werden ausschließlich
 * Feldnamen gezählt, nie Werte — und Suchbegriffe werden gar nicht gespeichert.
 */
export interface FormulareUndSucheProps {
  report: UsageFormReport;
}

/** Die häufigsten bemängelten Feldnamen aus dem JSON-Zähler — höchstens drei, absteigend. */
export const feldnamen = (wert: UsageExtra): string => {
  if (typeof wert !== 'object' || wert === null) return LEER;
  const paare = Object.entries(wert).filter(
    (eintrag): eintrag is [string, number] => typeof eintrag[1] === 'number' && Number.isFinite(eintrag[1]),
  );
  if (paare.length === 0) return LEER;
  return paare
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, 3)
    .map(([name]) => name)
    .join(', ');
};

export default function FormulareUndSuche({ report }: FormulareUndSucheProps) {
  const { forms, searches } = report;
  const suchKacheln = [
    {
      titel: 'Suchen',
      wert: formatCount(searches.count),
      unterzeile: 'Kopfzeile und Tabellen',
    },
    {
      titel: 'Ohne Treffer',
      wert: formatPercent(searches.zeroResultShare),
      unterzeile: 'Suchbegriffe werden nicht gespeichert',
    },
    {
      titel: 'Ø Rang der Auswahl',
      wert: formatNumber(searches.avgSelectedRank),
      unterzeile: '1 = erster Treffer',
    },
  ];

  return (
    <>
      <DatenTabelle
        leer={forms.length === 0}
        leerText="Im Zeitraum sind keine Formulare verdichtet."
        spalten={[
          { titel: 'Maske' },
          { titel: 'Geöffnet', numerisch: true },
          { titel: 'Abgeschickt', numerisch: true },
          { titel: 'Fehlgeschlagen', numerisch: true },
          { titel: 'Abbruchquote', numerisch: true },
          { titel: 'Ø Dauer', numerisch: true },
          { titel: 'Nutzer', numerisch: true },
          { titel: 'Häufigste ungültige Felder' },
        ]}
      >
        {forms.map((row, stelle) => (
          // eslint-disable-next-line react/no-array-index-key
          <TableRow key={`${row.label}-${stelle}`}>
            <TableCell>
              <SchutzZeile suppressed={row.suppressed} groups={row.groups} users={row.users} bezeichner={row.name} />
            </TableCell>
            <TableCell sx={zahlenZelle}>{formatCount(row.opens)}</TableCell>
            <TableCell sx={zahlenZelle}>{formatCount(row.submitOk)}</TableCell>
            <TableCell sx={zahlenZelle}>{formatCount(row.submitFail)}</TableCell>
            <TableCell sx={zahlenZelle}>{formatPercent(row.abandonRate)}</TableCell>
            <TableCell sx={zahlenZelle}>{formatDuration(row.avgOpenMs)}</TableCell>
            <TableCell sx={zahlenZelle}>{nutzerText(row.users, row.usersEstimated)}</TableCell>
            {/* Sammelzeilen tragen kein topInvalidFields (usage.graphql: dort immer null). */}
            <TableCell>{row.suppressed ? LEER : feldnamen(row.topInvalidFields)}</TableCell>
          </TableRow>
        ))}
      </DatenTabelle>

      {searches.suppressed ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Zur Suche sind im Zeitraum zu wenige verschiedene Personen erfasst; Kennzahlen entfallen.
        </Typography>
      ) : (
        <Grid container spacing={2} sx={{ mt: 0 }}>
          {suchKacheln.map(({ titel, wert, unterzeile }) => (
            <Grid item xs={12} sm={4} key={titel}>
              <Paper
                variant="outlined"
                data-testid="nutzung-suche-kachel"
                sx={{ p: 2, height: '100%', borderRadius: 2, bgcolor: 'background.default' }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}
                >
                  {titel}
                </Typography>
                <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                  {wert}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {unterzeile}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );
}
